import * as THREE from 'three';
import { Pass, makeRT, resizeRT } from './pass.js';

/**
 * Anti-aliasing: a sub-pixel-jittered temporal resolve with an FXAA-class
 * spatial pre-filter folded into the same pass.
 *
 * A thousand spear tips against a bright sky is the hardest AA case there is,
 * and it is the single loudest "this is a browser game" tell. One 8-tap Halton
 * jitter sequence on the projection matrix plus depth reprojection converges a
 * still frame to near-supersampled quality; the 3×3 neighbourhood clamp keeps
 * moving warriors from ghosting, and the spatial filter carries the frames
 * where the clamp has to throw the history away.
 *
 * Everything is done on a reversible tonemap (c / (1 + luma)) so a single HDR
 * spark cannot dominate the average.
 */

const FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D tCur;
  uniform sampler2D tHist;
  uniform sampler2D tDepth;
  uniform mat4 uProjInv;
  uniform mat4 uReproject;     // prevViewProj * cameraMatrixWorld
  uniform vec2 uTexel;
  uniform vec4 uParams;        // x blend, y spatial amount, z clampScale, w firstFrame
  varying vec2 vUv;

  float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
  vec3 tm(vec3 c)  { return c / (1.0 + luma(c)); }
  vec3 itm(vec3 c) { return c / max(1.0 - luma(c), 1e-4); }

  void main() {
    vec2 t = uTexel;
    vec3 cM = texture2D(tCur, vUv).rgb;
    vec3 cN = texture2D(tCur, vUv + vec2(0.0,  t.y)).rgb;
    vec3 cS = texture2D(tCur, vUv + vec2(0.0, -t.y)).rgb;
    vec3 cE = texture2D(tCur, vUv + vec2( t.x, 0.0)).rgb;
    vec3 cW = texture2D(tCur, vUv + vec2(-t.x, 0.0)).rgb;
    vec3 cNW = texture2D(tCur, vUv + vec2(-t.x,  t.y)).rgb;
    vec3 cNE = texture2D(tCur, vUv + vec2( t.x,  t.y)).rgb;
    vec3 cSW = texture2D(tCur, vUv + vec2(-t.x, -t.y)).rgb;
    vec3 cSE = texture2D(tCur, vUv + vec2( t.x, -t.y)).rgb;

    vec3 mM = tm(cM), mN = tm(cN), mS = tm(cS), mE = tm(cE), mW = tm(cW);
    vec3 mNW = tm(cNW), mNE = tm(cNE), mSW = tm(cSW), mSE = tm(cSE);

    // ---- spatial (FXAA-class) edge blend on the current frame -------------
    float lM = luma(mM), lN = luma(mN), lS = luma(mS), lE = luma(mE), lW = luma(mW);
    float lNW = luma(mNW), lNE = luma(mNE), lSW = luma(mSW), lSE = luma(mSE);
    float lMin = min(lM, min(min(lN, lS), min(lE, lW)));
    float lMax = max(lM, max(max(lN, lS), max(lE, lW)));
    float range = lMax - lMin;
    vec3 spatial = mM;
    if (range >= max(0.028, lMax * 0.135)) {
      vec2 dir;
      dir.x = -((lNW + lNE) - (lSW + lSE));
      dir.y =  ((lNW + lSW) - (lNE + lSE));
      float dirReduce = max((lNW + lNE + lSW + lSE) * 0.25 * 0.10, 1.0 / 128.0);
      float rcpMin = 1.0 / (min(abs(dir.x), abs(dir.y)) + dirReduce);
      dir = clamp(dir * rcpMin, -6.0, 6.0) * t;
      vec3 rgbA = 0.5 * (tm(texture2D(tCur, vUv + dir * (1.0 / 3.0 - 0.5)).rgb) +
                         tm(texture2D(tCur, vUv + dir * (2.0 / 3.0 - 0.5)).rgb));
      vec3 rgbB = rgbA * 0.5 + 0.25 * (tm(texture2D(tCur, vUv - dir * 0.5).rgb) +
                                       tm(texture2D(tCur, vUv + dir * 0.5).rgb));
      float lB = luma(rgbB);
      spatial = (lB < lMin || lB > lMax) ? rgbA : rgbB;
      spatial = mix(mM, spatial, uParams.y);
    }

    // ---- reprojection -----------------------------------------------------
    float d = texture2D(tDepth, vUv).x;
    vec4 vp = uProjInv * vec4(vUv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
    vec3 P = vp.xyz / vp.w;
    vec4 pc = uReproject * vec4(P, 1.0);
    vec2 prevUv = (pc.xy / pc.w) * 0.5 + 0.5;

    float w = uParams.x;
    if (prevUv.x < 0.0 || prevUv.x > 1.0 || prevUv.y < 0.0 || prevUv.y > 1.0) w = 0.0;
    w *= uParams.w;

    if (w > 0.001) {
      vec3 hist = tm(texture2D(tHist, prevUv).rgb);
      // variance clipping: tighter and far less blurry than a raw min/max box
      vec3 m1 = mM + mN + mS + mE + mW + mNW + mNE + mSW + mSE;
      vec3 m2 = mM * mM + mN * mN + mS * mS + mE * mE + mW * mW +
                mNW * mNW + mNE * mNE + mSW * mSW + mSE * mSE;
      vec3 mu = m1 / 9.0;
      vec3 sigma = sqrt(max(m2 / 9.0 - mu * mu, vec3(0.0)));
      vec3 lo = mu - sigma * uParams.z;
      vec3 hi = mu + sigma * uParams.z;
      vec3 clamped = clamp(hist, lo, hi);
      // how far the history had to be pulled → how much to trust it
      float rej = clamp(length(clamped - hist) * 3.2, 0.0, 1.0);
      w *= (1.0 - rej * 0.92);
      // Reprojection is camera-only: a warrior swinging at 8 m moves in screen
      // space even on a locked-off camera, and his history lands on whatever
      // was behind him. The clamp is the only thing that catches that, so a
      // pixel whose neighbourhood is *unstable* (large local variance, i.e.
      // armour scale detail and silhouette edges — exactly what smears) gives
      // up more of its history than a flat one does.
      float unstable = clamp(luma(sigma) * 7.0, 0.0, 1.0);
      float dev = clamp(length(mu - hist) * 2.6, 0.0, 1.0);
      w *= 1.0 - 0.55 * unstable * dev;
      // sub-pixel motion also lowers trust so fast pans do not smear
      float vel = length((prevUv - vUv) / max(t, vec2(1e-5)));
      w *= clamp(1.0 - vel * 0.010, 0.35, 1.0);
      gl_FragColor = vec4(itm(mix(spatial, clamped, w)), 1.0);
    } else {
      gl_FragColor = vec4(itm(spatial), 1.0);
    }
  }`;

/** Halton(2,3), the standard TAA jitter sequence. */
function halton(i, b) {
  let f = 1, r = 0, n = i;
  while (n > 0) { f /= b; r += f * (n % b); n = Math.floor(n / b); }
  return r;
}

export class TAA {
  constructor(composer, samples = 8) {
    this.composer = composer;
    this.history = [null, null];
    this.index = 0;
    this.frame = 0;
    this.samples = samples;
    this.jitter = new Float32Array(samples * 2);
    for (let i = 0; i < samples; i++) {
      this.jitter[i * 2] = halton(i + 1, 2) - 0.5;
      this.jitter[i * 2 + 1] = halton(i + 1, 3) - 0.5;
    }
    this.pass = new Pass(FRAG, {
      tCur: { value: null }, tHist: { value: null }, tDepth: { value: null },
      uProjInv: { value: new THREE.Matrix4() },
      uReproject: { value: new THREE.Matrix4() },
      uTexel: { value: new THREE.Vector2() },
      uParams: { value: new THREE.Vector4(0.88, 0.85, 1.00, 0) },
    });
    this._prevVP = new THREE.Matrix4();
    this._tmp = new THREE.Matrix4();
    this._valid = false;
  }

  setSize(w, h) {
    for (let i = 0; i < 2; i++) {
      if (!this.history[i]) this.history[i] = makeRT(w, h, { name: 'taa' + i });
      else if (resizeRT(this.history[i], w, h)) this._valid = false;
    }
    this.pass.uniforms.uTexel.value.set(1 / w, 1 / h);
  }

  /** Sub-pixel offset for this frame, in NDC. Apply before rendering. */
  jitterFor(frame, w, h, out) {
    const i = (frame % this.samples) * 2;
    out.set(this.jitter[i] * 2 / w, this.jitter[i + 1] * 2 / h);
    return out;
  }

  invalidate() { this._valid = false; }

  get ready() { return !!this.history[0]; }

  /** Resolve `srcTexture` against the history. @returns the resolved texture. */
  render(srcTexture, depthTexture, camera) {
    const dst = this.history[this.index];
    const prev = this.history[1 - this.index];
    const u = this.pass.uniforms;
    u.tCur.value = srcTexture;
    u.tHist.value = prev.texture;
    u.tDepth.value = depthTexture;
    u.uProjInv.value.copy(camera.projectionMatrixInverse);
    u.uReproject.value.multiplyMatrices(this._prevVP, camera.matrixWorld);
    u.uParams.value.w = this._valid ? 1 : 0;
    this.composer.run(this.pass, dst);
    this.index = 1 - this.index;
    this._valid = true;
    return dst.texture;
  }

  /** Store this frame's *unjittered* view-projection for the next reprojection. */
  capture(camera, unjitteredProj) {
    this._prevVP.multiplyMatrices(unjitteredProj, camera.matrixWorldInverse);
  }

  dispose() {
    this.history[0]?.dispose(); this.history[1]?.dispose();
    this.pass.dispose();
  }
}
